import {
  AddressVerification,
  BankReference,
  ConditionalOptionValues,
  ConditionalStepField,
  CreditApplicationFields,
  CreditApplicationStepField,
  CreditApplicationTemplate,
  DynamicInvitedSignaturesFields,
  ExtractedTradeReference,
  GroupedCreditApplicationFields,
  isFieldRequiredForAnswers,
  isFieldShownForAnswers,
} from '@dill/dill-shared';
import React, { useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Trash, PlusCircle, CheckCircle } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker, { parseDateValue } from './AppDatePicker';
import { AppCheckBox } from '../../../general/AppCheckBox/AppCheckBox';
import { DillButton } from '../../../general/Dill/DillButton';
import DocumentField from '../CreditApplicationComponents/DocumentField';
import RadioInputs from '../CreditApplicationComponents/RadioInput';
import { UploadFileInputs } from '../CreditApplicationComponents/UploadFileInput';
import InviteSignature from './InviteSignature';
import ConditionalOption from '../CreditApplicationComponents/ConditionalOption';
import AppBankReference from './AppBankReference';
import AppTradeReferenceUpload from './AppTradeReferenceUpload';
import { useAppSelector } from '../../../../redux/store';
import ConditionalOptionPreview, { ConditionalOptionPreviewRef } from './ConditionalOptionPreview';
import { useConditionalOption } from './useConditionalOption';
import StateDropDown from './StateDropDown';
import OptionsDropDown from './OptionsDropDown';
import CheckboxList from './CheckboxList';
import ConditionalForm from './ConditionalForm';
import { isHTML } from '../../../../utils/helpers';
import DynamicForm from './DynamicForm';
import { DillCheckbox } from '../../../general/Dill';
import TradeReferenceCompanySelect from './TradeReferenceCompanySelect';
import AddressAutocompleteSelect from './AddressAutocompleteSelect';
import {
  resolveFieldOptions,
  resolveDropdownFieldOptions,
  resolveSalesRepFieldLabel,
} from './conditionalOptionUtils';
import { getCreditApplicationTemplateLabel } from '../CreditApplicationTemplate/CONSTANTS';

// Thin wrapper that owns the state for one CONDITIONAL_SELECTED_FIELD via the
// useConditionalOption hook, then passes controlled props down to the pure renderer.
interface ConditionalOptionFieldProps {
  creditApplicationOptionData: Record<string, ConditionalStepField | null | undefined> | null;
  name: string;
  isRequired?: boolean;
  isRow?: boolean;
  errorText?: string;
  optionList?: string;
  externalSelectedOption: string;
  externalFieldValues: ConditionalOptionValues[];
  onChangeData: (data: { value: string; fieldValue: ConditionalOptionValues[] }) => void;
  branches?: { name: string; erpId?: string }[];
  salesReps?: { name: string; erpId?: string }[];
  externalSourceValues?: Record<string, unknown>;
}
const ConditionalOptionField = forwardRef<ConditionalOptionPreviewRef, ConditionalOptionFieldProps>(
  (
    {
      creditApplicationOptionData,
      name,
      isRequired,
      isRow,
      errorText,
      optionList,
      externalSelectedOption,
      externalFieldValues,
      onChangeData,
      branches,
      salesReps,
      externalSourceValues,
    },
    ref
  ) => {
    const {
      selectedOption,
      optionStates,
      errors,
      handleSelectOption,
      handleChangeValue,
      handleAddGroup,
      handleRemoveGroup,
      validate,
    } = useConditionalOption({
      creditApplicationOptionData,
      externalSelectedOption,
      externalFieldValues,
      onChangeData,
      externalSourceValues,
    });

    useImperativeHandle(ref, () => ({ validate }), [validate]);

    return (
      <ConditionalOptionPreview
        creditApplicationOptionData={creditApplicationOptionData}
        name={name}
        isRequired={isRequired}
        isRow={isRow}
        errorText={errorText}
        optionList={optionList}
        selectedOption={selectedOption}
        currentOptionState={selectedOption ? optionStates[selectedOption] ?? null : null}
        fieldErrors={errors}
        onSelectOption={handleSelectOption}
        onChangeValue={handleChangeValue}
        onAddGroup={handleAddGroup}
        onRemoveGroup={handleRemoveGroup}
        branches={branches}
        salesReps={salesReps}
        externalSourceValues={externalSourceValues}
      />
    );
  }
);

type DynamicStepFormProps = {
  title: string;
  stepFields: CreditApplicationStepField[];
  formData: {
    uniqueId: string;
    id: string;
    name: string;
    fromField: string;
    isRequired: boolean;
    isFieldEmail: boolean;
    optionType?: string;
    isHashed: boolean;
    stepName: string;
    stepNumber: number;
    groupStepPosition?: number | null | undefined;
    groupPosition?: number | null | undefined;
    groupedPosition?: number | null | undefined;
    groupName?: string | null;
    groupColumnType?: string;
    isFieldArray: boolean;
    value: string | number | boolean | File | any | null;
    secondaryText?: string;
    status?:
      | 'Pending'
      | 'Requested'
      | 'Submitted'
      | 'Rejected'
      | 'Blocked'
      | 'Dropped'
      | 'Delivered'
      | 'Deferred'
      | 'Bounce';
    emailDate?: string;
    sendEmail?: boolean;
    signatureGroupName?: string;
    placeHolder?: string;
    type?: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    minimumCreditAmount?: number;
    secondaryValue?: string | null;
    fieldValue?: ConditionalOptionValues[];
    formTemplate?: string | null;
    showIfNotPlaid?: boolean;
    linkedFieldId?: string | null;
    populateFieldSignatureId?: string | null;
    autoPopulateFieldIds?: string | null;
    copyFromFieldIds?: string | null;
    copyToFieldIds?: string | null;
    datePickerFormat?: string;
    validationType?: string;
    emailValidationType?: 'SINGLE' | 'MULTIPLE';
    maxLength?: number;
    currencyDecimalPlaces?: number;
    addressVerification?: AddressVerification | null;
  }[];
  errors: {
    uniqueId: string;
    errorText: string;
  }[];
  onChange: (data: {
    id: string;
    value: string | number | boolean | File | any | null;
    secondaryValue?: string | null;
    fieldValue?: ConditionalOptionValues[];
    addressVerification?: AddressVerification | null;
  }) => void;
  onAddGroupedField: (data: { groupName: string; position: number; count?: number }) => void;
  onRemoveGroupedField: (data: {
    groupName: string;
    position: number;
    groupPostion: number;
  }) => void;
  onUpdateInviteSigners: (
    data: {
      title: string;
      email: string;
      step: number;
      signatureGroupName?: string;
      placeHolder?: string;
      type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
      minimumCreditAmount?: number;
      fields: DynamicInvitedSignaturesFields[];
      minimumNumberOfSignatures?: number;
      id?: string;
    }[]
  ) => void;
  invitedSignatures: {
    title: string;
    email: string;
    step: number;
    signatureGroupName?: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    minimumCreditAmount?: number;
    fields: DynamicInvitedSignaturesFields[];
    minimumNumberOfSignatures?: number;
    signature?: string;
    dateSigned?: string;
    ipAddress?: string;
    location?: string;
  }[];
  invitedSignaturesErrors: { placeHolder: string; errorText: string }[];
  bankReference: BankReference[] | null;
  bankError: string | null;
  onUpdateBankReference: (
    data: BankReference[] | null | ((prev: BankReference[] | null) => BankReference[] | null)
  ) => void;
  bankReferenceRequiredFields?: string[];
  // Domains a manual bank reference's contact email may not be at (see BankDetailsModal).
  blockedBankReferenceEmailDomains?: string[];
  amountOfCredit?: number;
  domainDetails?: {
    id: string;
    domainId: string;
    url: string;
    name: string;
    branches: { name: string; erpId?: string }[];
    salesReps?: { name: string; erpId?: string; isHouseRep?: boolean }[];
  } | null;
  step?: number;
  templates?: CreditApplicationTemplate[];
  selectedTemplate?: CreditApplicationTemplate | null;
  onSelectTemplate?: (template: CreditApplicationTemplate) => void;
  showStepName?: boolean;
  enableAutofillCheckboxes?: boolean;
  onSignNow?: (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => void;
  onEditSignNow?: (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => void;
  onClearSignNow?: (field: {
    title: string;
    placeHolder?: string;
    type: 'AUTHORIZED_AGENT' | 'GUARANTOR' | 'OTHER' | 'PERSONAL' | 'CORPORATE_GUARANTOR';
    fields: DynamicInvitedSignaturesFields[];
    signatureGroupName?: string;
  }) => void;
  signedNowPlaceholders?: string[];
  senderEmail?: string;
};

const DynamicStepForm = forwardRef<ConditionalOptionPreviewRef, DynamicStepFormProps>(
  (
    {
      title,
      stepFields,
      formData,
      onChange,
      errors,
      onAddGroupedField,
      onRemoveGroupedField,
      onUpdateInviteSigners,
      invitedSignatures,
      invitedSignaturesErrors,
      bankReference,
      bankError,
      onUpdateBankReference,
      bankReferenceRequiredFields = [],
      blockedBankReferenceEmailDomains,
      amountOfCredit,
      domainDetails,
      step,
      templates,
      selectedTemplate,
      onSelectTemplate,
      showStepName = true,
      enableAutofillCheckboxes = false,
      onSignNow,
      onEditSignNow,
      onClearSignNow,
      signedNowPlaceholders = [],
      senderEmail,
    },
    ref
  ) => {
    const conditionalRefs = useRef<Record<string, ConditionalOptionPreviewRef | null>>({});
    const dynamicRefRefs = useRef<Record<string, ConditionalOptionPreviewRef | null>>({});
    const [inlineErrors, setInlineErrors] = useState<Record<string, string>>({});
    const [rawCurrencyText, setRawCurrencyText] = useState<Record<string, string>>({});

    const formatEIN = (raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 9);
      return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
    };

    const formatSSN = (raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 9);
      if (digits.length > 5)
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return digits;
    };

    const formatPhone = (raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 10);
      if (digits.length > 6)
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return digits;
    };

    useImperativeHandle(ref, () => ({
      validate: () => {
        const refs1 = Object.values(conditionalRefs.current);
        const refs2 = Object.values(dynamicRefRefs.current);

        if (refs1.length === 0 && refs2.length === 0) return true;

        const results1 = refs1.map((r) => r?.validate?.());
        const results2 = refs2.map((r) => r?.validate?.());

        return [...results1, ...results2].every((res) => res !== false);
      },
    }));
    const { basicSupplier } = useAppSelector((state) => state.buyers);
    const { creditApplication } = useAppSelector((state) => state.creditApplications);

    const getColumnsCount = (type: string) =>
      type === 'ONE_COLUMN' ? 1 : type === 'TWO_COLUMNS' ? 2 : 3;

    const bankRefField = formData.find((f) => f.fromField === 'BANK_REFERENCE');

    // Top-level answers by field id, so nested conditional fields can depend on them.
    const formSourceValues: Record<string, unknown> = Object.fromEntries(
      formData.map((f) => [f.id, f.value])
    );

    const isFieldVisible = (field: CreditApplicationFields): boolean =>
      isFieldShownForAnswers(field, (fieldId) => formData.find((f) => f.id === fieldId));

    function groupByGroupedPosition(data: GroupedCreditApplicationFields[]) {
      return data.reduce<Record<number, GroupedCreditApplicationFields[]>>((acc, item) => {
        const position = item.groupedPosition ?? 0;
        if (!acc[position]) {
          acc[position] = [];
        }
        acc[position].push(item);
        return acc;
      }, {});
    }

    // Finds a field by its plain id (e.g. 'company-city') rather than a computed uniqueId,
    // since a sibling field (city/state/zip, or a trade reference's paired email) can live
    // in a different row/step position than the field triggering the lookup. When the id is
    // repeated across multiple grouped instances, picks the one in the same group instance.
    const findSiblingFieldById = (
      candidateId: string,
      reference?: { groupedPosition?: number | null; groupStepPosition?: number | null }
    ) => {
      // Cloned groups (added via "Add Trade Reference") suffix their field ids with
      // `-group-N`, so match against the base id rather than the raw one.
      const candidates = formData.filter((f) => f.id.replace(/-group-\d+$/, '') === candidateId);
      if (candidates.length <= 1) return candidates[0];
      return (
        candidates.find(
          (f) =>
            f.groupedPosition === reference?.groupedPosition &&
            f.groupStepPosition === reference?.groupStepPosition
        ) ?? candidates[0]
      );
    };

    const applyExtractedTradeReferences = (
      nameFieldCandidates: typeof formData,
      references: ExtractedTradeReference[]
    ) => {
      references.slice(0, nameFieldCandidates.length).forEach((extracted, i) => {
        const nameField = nameFieldCandidates[i];
        const emailField = findSiblingFieldById('trade-reference-company-email', nameField);
        const phoneField = findSiblingFieldById('trade-reference-company-phone', nameField);
        const accountNumberField = findSiblingFieldById(
          'trade-reference-company-account-number',
          nameField
        );
        const addressField = findSiblingFieldById('trade-reference-company-address', nameField);
        const cityField = findSiblingFieldById('trade-reference-company-city', nameField);
        const stateField = findSiblingFieldById('trade-reference-company-state', nameField);
        const zipField = findSiblingFieldById('trade-reference-company-zip', nameField);

        onChange({ id: nameField.uniqueId, value: extracted.companyName ?? '' });
        if (emailField) onChange({ id: emailField.uniqueId, value: extracted.email ?? '' });
        if (phoneField) onChange({ id: phoneField.uniqueId, value: extracted.phone ?? '' });
        if (accountNumberField)
          onChange({
            id: accountNumberField.uniqueId,
            value: extracted.accountNumber ?? '',
          });
        if (addressField) onChange({ id: addressField.uniqueId, value: extracted.address ?? '' });
        if (cityField) onChange({ id: cityField.uniqueId, value: extracted.city ?? '' });
        if (stateField) onChange({ id: stateField.uniqueId, value: extracted.state ?? '' });
        if (zipField) onChange({ id: zipField.uniqueId, value: extracted.zip ?? '' });
      });
    };

    // When OCR extracts more trade references than there are slots for, we ask the parent
    // to grow the group (onAddGroupedField), then wait here for the newly cloned slots to
    // show up in `formData` before filling them in — the parent's state update is async, so
    // the slots aren't available on the same render that requested them.
    const [pendingTradeReferenceFills, setPendingTradeReferenceFills] = useState<
      {
        groupName: string | null | undefined;
        groupStepPosition: number | null | undefined;
        references: ExtractedTradeReference[];
      }[]
    >([]);

    useEffect(() => {
      if (pendingTradeReferenceFills.length === 0) return;
      pendingTradeReferenceFills.forEach(({ groupName, groupStepPosition, references }) => {
        const candidates = formData
          .filter(
            (f) =>
              f.groupName === groupName &&
              f.groupStepPosition === groupStepPosition &&
              /^trade-reference-company-name(-group-\d+)?$/.test(f.id)
          )
          .sort((a, b) => {
            const posA = Number(a.id.match(/-group-(\d+)$/)?.[1] ?? 0);
            const posB = Number(b.id.match(/-group-(\d+)$/)?.[1] ?? 0);
            return posA - posB;
          });
        if (candidates.length >= references.length) {
          applyExtractedTradeReferences(candidates, references);
          setPendingTradeReferenceFills((prev) =>
            prev.filter(
              (p) => !(p.groupName === groupName && p.groupStepPosition === groupStepPosition)
            )
          );
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, pendingTradeReferenceFills]);

    let anyFieldRendered = false;

    const renderFieldComponent = (
      fields: CreditApplicationFields[],
      groupName: string | null | undefined,
      groupColumnType: string | null | undefined,
      stepPosition: number | null | undefined,
      groupPostion: number
    ) => {
      return fields.map((templateField, idx) => {
        if (!isFieldVisible(templateField)) return null;
        // A conditionalRequired field shows as required only while its condition is met.
        const field = templateField.conditionalRequired?.sourceFieldId
          ? {
              ...templateField,
              isRequired: isFieldRequiredForAnswers(
                templateField,
                (id) => formData.find((f) => f.id === id)?.value
              ),
            }
          : templateField;

        let fieldComponent = null;
        const uniqueId =
          `${title} ${groupName} ${stepPosition} ${groupColumnType} ${groupPostion} ${
            field.id || ''
          }`
            .toLowerCase()
            .replace(/\s+/g, '-');
        const fieldFormData = formData.find((f) => f.uniqueId === uniqueId);
        const getValue = fieldFormData?.value;
        const getSecondaryValue = fieldFormData?.secondaryValue;
        const currencyDecimalPlaces: number =
          fieldFormData?.currencyDecimalPlaces ?? (field as any).currencyDecimalPlaces ?? 2;

        let fieldValue = fieldFormData?.fieldValue;

        if (fieldValue && field.fromField === 'DYNAMIC_FORM_DOCUMENT') {
          fieldValue = fieldValue.map((f) => {
            let val = f.value;
            const value = formData.find((ft) => f.linkedFieldId?.includes(ft.id))?.value;

            if ((f.value === '' || f.value === null) && value) {
              val = value;
            }
            return {
              ...f,
              value: val,
            };
          });
        }
        if (
          bankRefField?.linkedFieldId &&
          bankRefField?.linkedFieldId.includes(field.id) &&
          bankReference &&
          bankReference?.length > 0
        ) {
          const manualBankRef = bankReference.find((f) => f.connectionType === 'manual');
          if (!manualBankRef && bankRefField?.showIfNotPlaid) {
            return null;
          }
        }

        switch (field.fromField) {
          case 'TEXT':
          case 'NUMBER':
          case 'CURRENCY':
          case 'TEXT_AREA':
            if (field.id.includes('trade-reference-company-name')) {
              const emailField = findSiblingFieldById(
                'trade-reference-company-email',
                fieldFormData
              );

              const addressField = findSiblingFieldById(
                'trade-reference-company-address',
                fieldFormData
              );
              const cityField = findSiblingFieldById('trade-reference-company-city', fieldFormData);
              const stateField = findSiblingFieldById(
                'trade-reference-company-state',
                fieldFormData
              );
              const zipField = findSiblingFieldById('trade-reference-company-zip', fieldFormData);
              fieldComponent = (
                <TradeReferenceCompanySelect
                  label={field.name}
                  isRequired={field.isRequired}
                  value={typeof getValue === 'string' ? getValue : ''}
                  errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                  dataPrivate={field?.isHashed ? true : false}
                  onTextChange={(text) => onChange({ id: uniqueId, value: text })}
                  onSelectSuggestion={(suggestion) => {
                    onChange({ id: uniqueId, value: suggestion.name });
                    if (emailField) {
                      onChange({ id: emailField.uniqueId, value: suggestion.email });
                    }
                    if (addressField && suggestion.address) {
                      onChange({ id: addressField.uniqueId, value: suggestion.address });
                    }
                    if (cityField && suggestion.city) {
                      onChange({ id: cityField.uniqueId, value: suggestion.city });
                    }
                    if (stateField && suggestion.state) {
                      onChange({ id: stateField.uniqueId, value: suggestion.state });
                    }
                    if (zipField && suggestion.zip) {
                      onChange({ id: zipField.uniqueId, value: suggestion.zip });
                    }
                  }}
                />
              );
              break;
            }
            if (
              field.fromField === 'TEXT' &&
              (field.id || '').includes('-address') &&
              !(field.id || '').includes('email')
            ) {
              // A cloned group row's field id carries a "-group-N" suffix (see
              // findSiblingFieldById above — the 2nd/3rd Trade Reference, or any repeated
              // group past the first), so strip that before stripping "-address" — otherwise
              // idPrefix comes out wrong and no sibling ever matches past the first group.
              const idPrefix = (field.id || '')
                .replace(/-group-\d+$/, '')
                .slice(0, -'-address'.length);
              const cityField = findSiblingFieldById(`${idPrefix}-city`, fieldFormData);
              const stateField = findSiblingFieldById(`${idPrefix}-state`, fieldFormData);
              const zipField = findSiblingFieldById(`${idPrefix}-zip`, fieldFormData);
              const hasAddressSiblings = !!cityField;
              fieldComponent = (
                <AddressAutocompleteSelect
                  label={field.name}
                  isRequired={field.isRequired}
                  value={typeof getValue === 'string' ? getValue : ''}
                  errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                  dataPrivate={field?.isHashed ? true : false}
                  onTextChange={(text) => onChange({ id: uniqueId, value: text })}
                  onSelectSuggestion={(suggestion) => {
                    const streetLine = [suggestion.streetLine, suggestion.secondary]
                      .filter(Boolean)
                      .join(' ');
                    if (hasAddressSiblings) {
                      onChange({
                        id: uniqueId,
                        value: streetLine,
                      });
                      if (cityField) onChange({ id: cityField.uniqueId, value: suggestion.city });
                      if (stateField)
                        onChange({ id: stateField.uniqueId, value: suggestion.state });
                      if (zipField) onChange({ id: zipField.uniqueId, value: suggestion.zipcode });
                    }
                  }}
                />
              );
              break;
            }
            fieldComponent = (
              <>
                <AppInputField
                  id={field.id}
                  autoComplete={'new-password'}
                  label={field.name}
                  labelSubtext={field.labelSubtext ?? ''}
                  inputType={
                    field.fromField === 'TEXT_AREA'
                      ? 'textarea'
                      : field.fromField === 'NUMBER'
                      ? 'number'
                      : 'text'
                  }
                  value={
                    field.fromField === 'CURRENCY'
                      ? rawCurrencyText[uniqueId] !== undefined
                        ? rawCurrencyText[uniqueId]
                        : typeof getValue === 'number'
                        ? getValue.toLocaleString('en-US', {
                            minimumFractionDigits: currencyDecimalPlaces,
                            maximumFractionDigits: currencyDecimalPlaces,
                          })
                        : ''
                      : field.fromField === 'NUMBER' && typeof getValue === 'number'
                      ? getValue
                      : (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') &&
                        typeof getValue === 'string'
                      ? getValue
                      : field.fromField === 'NUMBER'
                      ? ''
                      : ''
                  }
                  isRequired={field.isRequired}
                  errorText={
                    errors.find((e) => e.uniqueId === uniqueId)?.errorText ||
                    inlineErrors[uniqueId] ||
                    ''
                  }
                  suffix={field.suffix}
                  prefix={field.fromField === 'CURRENCY' ? field.prefix || '$' : field.prefix}
                  placeholder={
                    field.validationType === 'EIN'
                      ? 'XX-XXXXXXX'
                      : field.validationType === 'SSN'
                      ? 'XXX-XX-XXXX'
                      : field.validationType === 'PHONE'
                      ? 'XXX-XXX-XXXX'
                      : undefined
                  }
                  maxLength={
                    field.validationType === 'EIN'
                      ? 10
                      : field.validationType === 'SSN'
                      ? 11
                      : field.validationType === 'PHONE'
                      ? 12
                      : field.maxLength
                  }
                  onTextChange={(text) => {
                    if (field.fromField === 'CURRENCY') {
                      const decPlaces: number = currencyDecimalPlaces;
                      const stripped = text.replace(/[$,]/g, '');
                      if (!/^\d*\.?\d*$/.test(stripped)) return;
                      if (decPlaces === 0 && stripped.includes('.')) return;
                      const decMatch = stripped.match(/\.(\d+)/);
                      if (decMatch && decMatch[1].length > decPlaces) return;
                      const intPart = stripped.split('.')[0];
                      const hasDecimal = stripped.includes('.');
                      const decPart = stripped.split('.')[1] ?? '';
                      const formattedInt = intPart
                        ? parseInt(intPart, 10).toLocaleString('en-US')
                        : '';
                      const displayValue = formattedInt + (hasDecimal ? '.' + decPart : '');
                      setRawCurrencyText((prev) => ({ ...prev, [uniqueId]: displayValue }));
                      const numericValue = parseFloat(stripped);
                      onChange({
                        id: uniqueId,
                        value: stripped === '' || isNaN(numericValue) ? null : numericValue,
                      });
                      return;
                    }
                    let formatted = text;
                    if (field.fromField === 'TEXT') {
                      if (field.validationType === 'EIN') formatted = formatEIN(text);
                      else if (field.validationType === 'SSN') formatted = formatSSN(text);
                      else if (field.validationType === 'PHONE') formatted = formatPhone(text);
                    }
                    // Real-time validation once full length is reached
                    if (field.fromField === 'TEXT') {
                      if (field.validationType === 'EIN' && formatted.length === 10) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{2}-\d{7}$/.test(formatted)
                            ? ''
                            : 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
                        }));
                      } else if (field.validationType === 'SSN' && formatted.length === 11) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{2}-\d{4}$/.test(formatted)
                            ? ''
                            : 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
                        }));
                      } else if (field.validationType === 'PHONE' && formatted.length === 12) {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{3}-\d{4}$/.test(formatted)
                            ? ''
                            : 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
                        }));
                      } else if (
                        (field.validationType === 'EIN' ||
                          field.validationType === 'SSN' ||
                          field.validationType === 'PHONE') &&
                        inlineErrors[uniqueId]
                      ) {
                        setInlineErrors((prev) => ({ ...prev, [uniqueId]: '' }));
                      }
                    }
                    onChange({
                      id: uniqueId,
                      value:
                        field.fromField === 'NUMBER' ? (text ? parseFloat(text) : null) : formatted,
                    });
                  }}
                  onBlur={() => {
                    if (field.fromField === 'CURRENCY') {
                      setRawCurrencyText((prev) => {
                        const next = { ...prev };
                        delete next[uniqueId];
                        return next;
                      });
                      return;
                    }
                    if (
                      (field.fromField === 'TEXT' || field.fromField === 'TEXT_AREA') &&
                      typeof getValue === 'string'
                    ) {
                      let val = getValue.trim();
                      if (field.isFieldEmail) {
                        if (field.emailValidationType === 'MULTIPLE') {
                          val = val
                            .split(/[,;]/)
                            .map((e: string) => e.trim().toLowerCase())
                            .filter(Boolean)
                            .join(', ');
                        } else {
                          val = val.toLowerCase();
                        }
                      }
                      onChange({ id: uniqueId, value: val });

                      // Catch incomplete/malformed value on blur
                      if (val && field.validationType === 'EIN') {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{2}-\d{7}$/.test(val)
                            ? ''
                            : 'Must be in XX-XXXXXXX format (e.g. 12-3456789)',
                        }));
                      } else if (val && field.validationType === 'SSN') {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{2}-\d{4}$/.test(val)
                            ? ''
                            : 'Must be in XXX-XX-XXXX format (e.g. 123-45-6789)',
                        }));
                      } else if (val && field.validationType === 'PHONE') {
                        setInlineErrors((prev) => ({
                          ...prev,
                          [uniqueId]: /^\d{3}-\d{3}-\d{4}$/.test(val)
                            ? ''
                            : 'Must be in XXX-XXX-XXXX format (e.g. 555-123-4567)',
                        }));
                      }
                    }
                  }}
                  labelTextClassName="whitespace-pre-wrap"
                  dataPrivate={field?.isHashed ? true : false}
                />
                {field.validationType === 'EIN' && !inlineErrors[uniqueId] && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Format: XX-XXXXXXX (e.g. 12-3456789)
                  </p>
                )}
                {field.validationType === 'SSN' && !inlineErrors[uniqueId] && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Format: XXX-XX-XXXX (e.g. 123-45-6789)
                  </p>
                )}
                {field.validationType === 'PHONE' && !inlineErrors[uniqueId] && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Format: XXX-XXX-XXXX (e.g. 555-123-4567)
                  </p>
                )}
                {field.emailValidationType === 'MULTIPLE' && (
                  <p className="text-xs mt-0.5 text-gray-400">
                    Separate multiple emails with a comma or semicolon
                  </p>
                )}
              </>
            );
            break;

          case 'DATE':
            fieldComponent = (
              <AppDatePicker
                label={field.name}
                labelSubtext={field.labelSubtext ?? ''}
                selectedDate={parseDateValue(getValue)}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                isRequired={field.isRequired}
                showMonthYearPicker={field.datePickerFormat === 'MONTH_YEAR'}
                onDateSelected={(date) => {
                  onChange({
                    id: uniqueId,
                    value: date,
                  });
                }}
              />
            );
            break;

          case 'CHECK_BOX':
            const errorsText = errors.find((e) => e.uniqueId === uniqueId)?.errorText || '';
            fieldComponent = (
              <div>
                <div
                  className={`my-1 flex items-center w-full ${
                    field.alignment === 'RIGHT'
                      ? 'justify-end'
                      : field.alignment === 'CENTER'
                      ? 'justify-center'
                      : 'justify-start'
                  }`}>
                  <AppCheckBox
                    className="cursor-pointer"
                    isChecked={typeof getValue === 'boolean' ? getValue : false}
                    onClick={(checked: boolean) => {
                      onChange({ id: uniqueId, value: checked });
                    }}
                  />
                  <div className="ml-2 text-sm">{field.name}</div>
                </div>
                {field.labelSubtext &&
                  (isHTML(field.labelSubtext) ? (
                    <p
                      className={`text-2xs ml-2 GREY_500-CLR w-full ${
                        field.alignment === 'RIGHT'
                          ? 'text-end'
                          : field.alignment === 'CENTER'
                          ? 'text-center'
                          : 'text-start'
                      }`}
                      dangerouslySetInnerHTML={{ __html: field.labelSubtext }}
                    />
                  ) : (
                    <p
                      className={`text-2xs ml-2 GREY_500-CLR w-full ${
                        field.alignment === 'RIGHT'
                          ? 'text-end'
                          : field.alignment === 'CENTER'
                          ? 'text-center'
                          : 'text-start'
                      }`}>
                      {field.labelSubtext}
                    </p>
                  ))}
                {errorsText && (
                  <p
                    className={`text-2xs ml-2 text-red-500 w-full ${
                      field.alignment === 'RIGHT'
                        ? 'text-end'
                        : field.alignment === 'CENTER'
                        ? 'text-center'
                        : 'text-start'
                    }`}>
                    {errorsText}
                  </p>
                )}
              </div>
            );
            break;

          case 'FILE':
            fieldComponent = (
              <div className="my-1">
                <UploadFileInputs
                  isRequired={field.isRequired}
                  errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                  label={field.name}
                  labelSubtext={field.labelSubtext ?? ''}
                  labelNode={
                    <span className="flex items-center gap-1 text-xs GREY_800-CLR">
                      <span>
                        {field.isRequired && <span className="ERROR_500-CLR mr-0.5">*</span>}
                        {field.name}
                      </span>
                      {field.sampleFile?.url && (
                        <a
                          href={field.sampleFile.url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-normal text-blue-500 underline">
                          (View sample)
                        </a>
                      )}
                    </span>
                  }
                  selectedFile={
                    typeof getValue === 'object' && getValue instanceof File ? getValue : null
                  }
                  selectedFileName={typeof getValue === 'object' ? getValue?.name : null}
                  onSelectFile={(data) => {
                    onChange({
                      id: uniqueId,
                      value: data.file,
                    });
                  }}
                  onRemoveFile={() => onChange({ id: uniqueId, value: null })}
                />
              </div>
            );
            break;

          case 'OPTION_FIELD':
            fieldComponent = (
              <div className="my-1 flex items-center">
                <RadioInputs
                  label={resolveSalesRepFieldLabel(field, getValue, domainDetails?.salesReps)}
                  labelSubtext={field.labelSubtext ?? ''}
                  onChange={(option) => {
                    onChange({
                      id: uniqueId,
                      value: option.value,
                    });
                  }}
                  id={field.name}
                  options={resolveFieldOptions(
                    field,
                    domainDetails?.branches,
                    domainDetails?.salesReps
                  )}
                  optionDescriptions={
                    field?.optionDescriptions
                      ? field.optionDescriptions.split(';;').map((option) => option.trim())
                      : undefined
                  }
                  isRequired={field.isRequired}
                  selectedOption={typeof getValue === 'string' ? getValue : ''}
                  className={field.isRow ? 'gap-1 md:gap-2 flex items-center' : ''}
                  errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                />
              </div>
            );
            break;

          case 'DOCUMENT_FIELD':
            fieldComponent = (
              <div className="my-1 flex items-center w-full">
                <DocumentField
                  text={field.documentText || ''}
                  className={`w-full ${field.fontWeight} text-[${field.fontSize ?? '12'}]px ${
                    field.alignment === 'RIGHT'
                      ? 'text-right'
                      : field.alignment === 'CENTER'
                      ? 'text-center'
                      : 'text-left'
                  }`}
                />
              </div>
            );
            break;

          case 'INVITE_USER':
            const foundForm = formData.find((fld) => fld.id === field?.linkedFieldId);
            let formTemplate = null;
            if (foundForm && foundForm.formTemplate) {
              formTemplate = foundForm.formTemplate.replace(
                /{{\s*([^}]+)\s*}}/g,
                '<span class="inline-block w-40"></span>'
              );
            }

            const shouldRenderInviteSignature =
              !!field.conditionalVisibility?.sourceFieldId ||
              !foundForm ||
              (foundForm?.value === null && foundForm?.secondaryValue === 'Yes');
            fieldComponent = shouldRenderInviteSignature ? (
              <div className="w-full">
                {formTemplate && (
                  <div>
                    {isHTML(formTemplate) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formTemplate,
                        }}
                      />
                    ) : (
                      <p>{formTemplate}</p>
                    )}
                  </div>
                )}

                <InviteSignature
                  data={
                    field?.whoNeedsToSign
                      ? field.whoNeedsToSign.split(',').map((option) => ({
                          title: option.trim(),
                          email: '',
                          step: 0,
                          signatureGroupName: field.name,
                          placeHolder: option.trim(),
                          type: field?.type ?? 'AUTHORIZED_AGENT',
                          minimumCreditAmount: field?.minimumCreditAmount,
                          fields: field.invitedSignaturesFields || [],
                          minimumNumberOfSignatures: field?.minimumNumberOfSignatures,
                          isRequired: field.isRequired,
                          isOptional:
                            !!templateField.conditionalRequired?.sourceFieldId && !field.isRequired,
                          id: field.id,
                        }))
                      : ([] as {
                          title: string;
                          email: string;
                          step: number;
                          signatureGroupName: string;
                          placeHolder: string;
                          type:
                            | 'AUTHORIZED_AGENT'
                            | 'GUARANTOR'
                            | 'OTHER'
                            | 'PERSONAL'
                            | 'CORPORATE_GUARANTOR';
                          minimumCreditAmount?: number;
                          fields: DynamicInvitedSignaturesFields[];
                          minimumNumberOfSignatures?: number;
                          isRequired?: boolean;
                          id?: string;
                        }[])
                  }
                  onChange={(data) => onUpdateInviteSigners(data)}
                  fieldErrors={invitedSignaturesErrors}
                  value={invitedSignatures}
                  amountOfCredit={amountOfCredit}
                  allowSignNow={!!onSignNow}
                  signedNowPlaceholders={signedNowPlaceholders}
                  onSignNow={onSignNow}
                  onEditSignNow={onEditSignNow}
                  onClearSignNow={onClearSignNow}
                  senderEmail={senderEmail}
                />
              </div>
            ) : null;

            break;

          case 'CONDITIONAL_OPTION':
            fieldComponent = (
              <ConditionalOption
                optionType={field?.optionType ?? ''}
                id={field.id}
                onChange={(data) => {
                  onChange({
                    id: uniqueId,
                    value: data.value,
                    secondaryValue: data.secondaryValue,
                  });
                }}
                isRequired={field.isRequired}
                label={field.name}
                selectedValue={{ value: getValue, secondaryValue: getSecondaryValue }}
                secondaryText={field.secondaryText}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                currentValue={
                  typeof getValue === 'object'
                    ? getValue?.name
                    : typeof getValue === 'string'
                    ? getValue
                    : null
                }
              />
            );
            break;

          case 'BANK_REFERENCE':
            fieldComponent = (
              <AppBankReference
                onSubmit={onUpdateBankReference}
                bankDetails={bankReference}
                bankError={bankError}
                bankReferenceRequiredFields={bankReferenceRequiredFields}
                supplierName={basicSupplier?.name ?? 'Supplier'}
                checkRequired={field.isRequired}
                onlyShowPlaidOption={field.onlyShowPlaidOption}
                blockedEmailDomains={blockedBankReferenceEmailDomains}
              />
            );
            break;

          case 'TRADE_REFERENCE': {
            const nameFieldCandidates = formData
              .filter((f) => /^trade-reference-company-name(-group-\d+)?$/.test(f.id))
              .sort((a, b) => {
                const posA = Number(a.id.match(/-group-(\d+)$/)?.[1] ?? 0);
                const posB = Number(b.id.match(/-group-(\d+)$/)?.[1] ?? 0);
                return posA - posB;
              });

            fieldComponent = (
              <AppTradeReferenceUpload
                value={getValue ?? null}
                onChange={(val) => onChange({ id: uniqueId, value: val })}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                isRequired={field.isRequired}
                supplierId={basicSupplier?.id ?? ''}
                maxReferenceSlots={nameFieldCandidates.length}
                onAcceptExtractedReferences={(references) =>
                  applyExtractedTradeReferences(nameFieldCandidates, references)
                }
              />
            );
            break;
          }

          case 'CONDITIONAL_SELECTED_FIELD': {
            // Key switches "empty" → "selected" when the draft option first loads,
            // forcing a remount so the lazy initializer sees the correct data.
            // It stays "selected" across option switches and sub-field edits so
            // those don't cause unnecessary remounts.
            const conditionalKey =
              typeof getValue === 'string' && getValue
                ? `${uniqueId}-opt-selected`
                : `${uniqueId}-opt-empty`;
            fieldComponent = (
              <ConditionalOptionField
                key={conditionalKey}
                ref={(el) => {
                  conditionalRefs.current[uniqueId] = el;
                }}
                externalSelectedOption={typeof getValue === 'string' ? getValue : ''}
                externalFieldValues={fieldValue ?? []}
                creditApplicationOptionData={field?.conditionalOptionFields || null}
                name={field.name}
                isRequired={field.isRequired}
                isRow={field.isRow}
                optionList={field.optionList}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onChangeData={(data) => {
                  onChange({
                    id: uniqueId,
                    value: data.value,
                    fieldValue: data.fieldValue ?? [],
                  });
                }}
                branches={domainDetails?.branches}
                salesReps={domainDetails?.salesReps}
                externalSourceValues={formSourceValues}
              />
            );
            break;
          }

          case 'STATE_DROPDOWN':
            fieldComponent = (
              <StateDropDown
                isRequired={field.isRequired}
                label={field.name}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value: text,
                  });
                }}
              />
            );
            break;

          case 'CHECKBOX_LIST':
            fieldComponent = (
              <CheckboxList
                isRequired={field.isRequired}
                label={resolveSalesRepFieldLabel(field, getValue, domainDetails?.salesReps)}
                options={resolveFieldOptions(
                  field,
                  domainDetails?.branches,
                  domainDetails?.salesReps
                )}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value: text,
                  });
                }}
              />
            );
            break;

          case 'DROPDOWN':
            fieldComponent = (
              <OptionsDropDown
                isRequired={field.isRequired}
                label={resolveSalesRepFieldLabel(field, getValue, domainDetails?.salesReps)}
                options={resolveDropdownFieldOptions(
                  field,
                  domainDetails?.branches,
                  domainDetails?.salesReps
                )}
                value={typeof getValue === 'string' ? getValue : ''}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                onTextChange={(text) => {
                  onChange({
                    id: uniqueId,
                    value: text,
                  });
                }}
              />
            );
            break;

          case 'CONDITIONAL_FORM':
            fieldComponent = (
              <ConditionalForm
                optionType={field?.optionType ?? ''}
                id={field.id}
                onChange={(data) => {
                  onChange({
                    id: uniqueId,
                    value: data.value,
                    secondaryValue: data.secondaryValue,
                  });
                }}
                isRequired={field.isRequired}
                label={field.name}
                selectedValue={{ value: getValue, secondaryValue: getSecondaryValue }}
                secondaryText={field.secondaryText}
                errorText={errors.find((e) => e.uniqueId === uniqueId)?.errorText || ''}
                currentValue={
                  typeof getValue === 'object'
                    ? getValue?.name
                    : typeof getValue === 'string'
                    ? getValue
                    : null
                }
              />
            );
            break;

          case 'DYNAMIC_FORM_DOCUMENT': {
            const autoPopulateValues: Record<string, string | number | boolean | null> = {};
            formData.forEach((f) => {
              if (typeof f.value !== 'object' || f.value === null) {
                autoPopulateValues[f.id] = f.value as string | number | boolean | null;
              }
            });
            if (field.autoPopulateFieldIds) {
              field.autoPopulateFieldIds
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean)
                .forEach((autoId) => {
                  const autoField = formData.find((f) => f.id === autoId);
                  if (autoField !== undefined) {
                    autoPopulateValues[autoId] = autoField.value as
                      | string
                      | number
                      | boolean
                      | null;
                  }
                });
            }
            fieldComponent = (
              <DynamicForm
                ref={(el) => {
                  dynamicRefRefs.current[uniqueId] = el;
                }}
                selectedValueOption={typeof getValue === 'string' ? getValue : ''}
                selectedFieldValue={fieldValue ?? []}
                creditApplicationOptionData={field?.conditionalOptionFields || null}
                name={field.name}
                formTemplate={field.formTemplate}
                autoPopulateValues={autoPopulateValues}
                onChangeData={(data) => {
                  onChange({
                    id: uniqueId,
                    value: data.value,
                    fieldValue: data.fieldValue ?? [],
                  });
                }}
              />
            );
            break;
          }

          case 'AUTOFILL_CHECKBOX': {
            if (!enableAutofillCheckboxes) {
              fieldComponent = null;
              break;
            }
            const autofillErrText = errors.find((e) => e.uniqueId === uniqueId)?.errorText || '';
            const autofillSourceIds = (
              fieldFormData?.copyFromFieldIds ??
              field.copyFromFieldIds ??
              ''
            )
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            const autofillTargetIds = (fieldFormData?.copyToFieldIds ?? field.copyToFieldIds ?? '')
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean);
            const autofillDataMatchesSource =
              autofillTargetIds.length > 0 &&
              autofillTargetIds.every((targetId, i) => {
                const sourceId = autofillSourceIds[i] ?? autofillSourceIds[0];
                const sourceValue = formData.find((f) => f.id === sourceId)?.value ?? '';
                const targetValue = formData.find((f) => f.id === targetId)?.value ?? '';
                return sourceValue === targetValue;
              }) &&
              autofillSourceIds.some((sourceId) => {
                const sourceValue = formData.find((f) => f.id === sourceId)?.value;
                return sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
              });
            const isAutofillChecked =
              (typeof getValue === 'boolean' ? getValue : false) || autofillDataMatchesSource;
            fieldComponent = (
              <div>
                <div className="my-1 flex items-center w-full">
                  <DillCheckbox
                    className="cursor-pointer"
                    isChecked={isAutofillChecked}
                    onChange={(checked: boolean) => {
                      onChange({ id: uniqueId, value: checked });
                    }}
                  />
                  <div className="ml-2 text-sm">{field.name}</div>
                </div>
                {field.labelSubtext &&
                  (isHTML(field.labelSubtext) ? (
                    <p
                      className="text-2xs ml-2 GREY_500-CLR w-full text-start"
                      dangerouslySetInnerHTML={{ __html: field.labelSubtext }}
                    />
                  ) : (
                    <p className="text-2xs ml-2 GREY_500-CLR w-full text-start">
                      {field.labelSubtext}
                    </p>
                  ))}
                {autofillErrText && <p className="text-2xs ml-2 text-red-500">{autofillErrText}</p>}
              </div>
            );
            break;
          }

          default:
            fieldComponent = (
              <div className="text-sm text-gray-400">{`${field.fromField} to be added`}</div>
            );
            break;
        }

        if (fieldComponent === null) return null;
        anyFieldRendered = true;

        return (
          <div
            key={idx}
            className={`
              flex-shrink-0
              flex-grow
              basis-[280px]
              sm:basis-[45%]
              lg:basis-[30%]
              max-w-full
            `}>
            <div className="py-2 w-full">{fieldComponent}</div>
          </div>
        );
      });
    };

    return (
      <div className="mb-2 md:mb-4">
        {showStepName && (
          <h2 className="text-base md:text-lg font-semibold mb-2 md:mb-4">{title}</h2>
        )}
        {step === 0 && templates && templates.length > 0 && (
          <div className="mb-4">
            <p className="text-xs mb-2 text-left font-medium">Select Application</p>
            <div className="flex flex-wrap gap-4">
              {templates.map((t) => (
                <label
                  key={t.id}
                  className={`flex items-center gap-1 cursor-pointer transition-colors`}>
                  <input
                    type="radio"
                    name="template"
                    checked={selectedTemplate?.id === t.id}
                    onChange={() => onSelectTemplate?.(t)}
                    className="w-3.5 h-3.5 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-xs text-gray-700">
                    {getCreditApplicationTemplateLabel(t)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
        {(() => {
          const stepFieldPositionByFieldId: Record<string, number> = {};
          stepFields.forEach((sf, idx) => {
            (sf.unGroupedFields || []).forEach((f) => {
              stepFieldPositionByFieldId[f.id] = idx;
            });
            (sf.groupedFields || []).forEach((gf) => {
              gf.fields.forEach((f) => {
                stepFieldPositionByFieldId[f.id] = idx;
              });
            });
          });

          const clusterIdByStepFieldPosition: number[] = new Array(stepFields.length).fill(-1);
          {
            let nextClusterId = 0;
            let skipUntilIdx = -1;
            stepFields.forEach((sf, idx) => {
              if (idx <= skipUntilIdx) return;
              if (sf.groupName) return;
              const borderId = (sf.unGroupedFields || []).find((f) => f.borderId)?.borderId;
              if (!borderId) return;
              const endIdx = stepFieldPositionByFieldId[borderId];
              if (endIdx === undefined || endIdx < idx) return;
              const clusterId = nextClusterId++;
              for (let i = idx; i <= endIdx; i++) {
                clusterIdByStepFieldPosition[i] = clusterId;
              }
              skipUntilIdx = endIdx;
            });
          }

          const mappedStepFields = stepFields.map((stepField, index) => {
            const groupedFields = groupByGroupedPosition(stepField.groupedFields || []);
            let minimumNumber = stepField.minimunNumberOfGroups || 1;
            if (
              creditApplication?.groupedSpecialConditions &&
              creditApplication.groupedSpecialConditions.length > 0
            ) {
              const foundCondition = creditApplication.groupedSpecialConditions.find(
                (condition) => condition.name === stepField.groupName
              );
              if (foundCondition) {
                minimumNumber = foundCondition.minimumNumber || 1;
              }
            }
            const groupSpecificRule = (creditApplication?.specificRules ?? []).find(
              (r) => r.fieldId === `group:${stepField.groupName}`
            );
            if (groupSpecificRule?.minimumNumberOfGroups !== undefined) {
              minimumNumber = groupSpecificRule.minimumNumberOfGroups;
            }

            const node = (
              <div key={'step-' + index + '-stepField-' + `${stepField?.groupName ?? ''}`}>
                {stepField.stepType === 'GROUPED' &&
                  stepField.hasTradeReferenceUpload &&
                  (() => {
                    const ocrUniqueId =
                      `${title} ${stepField.groupName} ${stepField.position} ${stepField.columnType} 0 ocr-upload`
                        .toLowerCase()
                        .replace(/\s+/g, '-');
                    const nameFieldCandidates = formData
                      .filter(
                        (f) =>
                          f.groupName === stepField.groupName &&
                          f.groupStepPosition === stepField.position &&
                          /^trade-reference-company-name(-group-\d+)?$/.test(f.id)
                      )
                      .sort((a, b) => {
                        const posA = Number(a.id.match(/-group-(\d+)$/)?.[1] ?? 0);
                        const posB = Number(b.id.match(/-group-(\d+)$/)?.[1] ?? 0);
                        return posA - posB;
                      });
                    return (
                      <div className="mb-2">
                        <AppTradeReferenceUpload
                          value={formData.find((f) => f.uniqueId === ocrUniqueId)?.value ?? null}
                          onChange={(val) => onChange({ id: ocrUniqueId, value: val })}
                          errorText={
                            errors.find((e) => e.uniqueId === ocrUniqueId)?.errorText || ''
                          }
                          supplierId={basicSupplier?.id ?? ''}
                          maxReferenceSlots={Object.values(groupedFields).length}
                          autoAddsExtraSlots={stepField.isFieldArray}
                          onAcceptExtractedReferences={(references) => {
                            const missingSlots = references.length - nameFieldCandidates.length;
                            if (stepField.isFieldArray && missingSlots > 0) {
                              onAddGroupedField({
                                groupName: stepField.groupName || '',
                                position: stepField.position || 0,
                                count: missingSlots,
                              });
                              setPendingTradeReferenceFills((prev) => [
                                ...prev.filter(
                                  (p) =>
                                    !(
                                      p.groupName === stepField.groupName &&
                                      p.groupStepPosition === stepField.position
                                    )
                                ),
                                {
                                  groupName: stepField.groupName,
                                  groupStepPosition: stepField.position,
                                  references,
                                },
                              ]);
                            } else {
                              applyExtractedTradeReferences(nameFieldCandidates, references);
                            }
                          }}
                        />
                      </div>
                    );
                  })()}
                {stepField.stepType === 'GROUPED' &&
                  stepField.groupedFields &&
                  stepField.groupedFields.some((gf) => gf.fields.some(isFieldVisible)) && (
                    <div className="PRIMARY_100-BG rounded-md">
                      <p className="text-sm font-bold ml-1 md:ml-2 p-1 md:p-2">
                        {stepField.groupName}
                      </p>
                      {groupedFields &&
                        Object.values(groupedFields).length > 0 &&
                        Object.values(groupedFields).map((groupField, j) => {
                          const hasVisibleField = groupField.some((gf) =>
                            gf.fields.some(isFieldVisible)
                          );
                          if (!hasVisibleField) return null;
                          return (
                            <div
                              key={`group-${stepField.groupName}-${j}` + index}
                              className="w-full border-b GREY_300-BORDER  p-1 md:p-2">
                              {stepField.isFieldArray &&
                                Object.values(groupedFields).length > minimumNumber && (
                                  <div
                                    className="w-full flex mb-1 md:mb-2 justify-end cursor-pointer text-red-500 items-center px-2"
                                    onClick={() =>
                                      onRemoveGroupedField({
                                        groupName: stepField.groupName || '',
                                        position: stepField.position || 0,
                                        groupPostion: groupField[0].groupedPosition || 0,
                                      })
                                    }>
                                    <div className="text-sm mr-1">Remove</div>
                                    <Trash color={COLORS.ERROR_400} size={16} />
                                  </div>
                                )}
                              {groupField.map((field, findex) => {
                                const columns = getColumnsCount(field.columnType);
                                const fieldsArray = Array(columns)
                                  .fill(null)
                                  .map(
                                    (_, idx) =>
                                      field.fields[idx] || {
                                        name: '',
                                        id: '',
                                        fromField: 'TEXT',
                                        isRequired: false,
                                        prefix: '',
                                        suffix: '',
                                      }
                                  );
                                return (
                                  <div key={`${field.id}-${findex}`} className="w-full">
                                    <div className="flex flex-wrap gap-4 md:gap-2">
                                      {renderFieldComponent(
                                        fieldsArray,
                                        stepField.groupName,
                                        stepField.columnType,
                                        stepField.position,
                                        field.groupedPosition
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })}

                      {stepField.isFieldArray && (
                        <div className="w-full flex mb-2 justify-end py-1 md:py-2 px-2 md:px-4">
                          <DillButton
                            label={stepField.moreButtonName || `Add ${stepField.groupName}`}
                            onClick={() => {
                              onAddGroupedField({
                                groupName: stepField.groupName || '',
                                position: stepField.position || 0,
                              });
                            }}
                            icon={<PlusCircle size={12} />}
                            height={30}
                          />
                        </div>
                      )}
                    </div>
                  )}
                {stepField.stepType === 'NOT_GROUPED' &&
                  stepField.unGroupedFields &&
                  stepField.unGroupedFields.some(isFieldVisible) && (
                    <div className="flex flex-wrap gap-4 md:gap-2">
                      {renderFieldComponent(
                        stepField.unGroupedFields,
                        stepField.groupName,
                        stepField.columnType,
                        stepField.position,
                        0
                      )}
                    </div>
                  )}
              </div>
            );

            // Whether anything in this section shows, so a bordered group whose sections are all
            // hidden (conditional visibility) isn't drawn as an empty box.
            const isVisible =
              stepField.stepType === 'GROUPED'
                ? !!stepField.hasTradeReferenceUpload ||
                  (stepField.groupedFields ?? []).some((gf) => gf.fields.some(isFieldVisible))
                : (stepField.unGroupedFields ?? []).some(isFieldVisible);

            return {
              node,
              isVisible,
              matchedBorderGroupIdx: clusterIdByStepFieldPosition[index] ?? -1,
            };
          });

          const renderedStepFields: JSX.Element[] = [];
          {
            let gsi = 0;
            while (gsi < mappedStepFields.length) {
              const { matchedBorderGroupIdx } = mappedStepFields[gsi];
              if (matchedBorderGroupIdx === -1) {
                renderedStepFields.push(mappedStepFields[gsi].node);
                gsi++;
                continue;
              }
              const clusterStart = gsi;
              while (
                gsi < mappedStepFields.length &&
                mappedStepFields[gsi].matchedBorderGroupIdx === matchedBorderGroupIdx
              )
                gsi++;
              const cluster = mappedStepFields.slice(clusterStart, gsi);
              if (!cluster.some((m) => m.isVisible)) continue;
              renderedStepFields.push(
                <div
                  key={`bordered-step-field-wrap-${matchedBorderGroupIdx}-${clusterStart}`}
                  className="border border-gray-200 rounded-lg p-2 flex flex-col gap-1 mb-2">
                  {cluster.map((m) => m.node)}
                </div>
              );
            }
          }

          if (!anyFieldRendered) {
            return (
              <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
                <div className="rounded-full SUCCESS_100-BG SUCCESS_500-CLR h-10 w-10 flex justify-center items-center">
                  <CheckCircle size={20} weight="fill" />
                </div>
                <p className="text-sm font-semibold">Nothing needed here</p>
                <p className="text-xs text-gray-500">
                  This step doesn&apos;t require any action — click Next to continue.
                </p>
              </div>
            );
          }

          return renderedStepFields;
        })()}
      </div>
    );
  }
);

export default DynamicStepForm;
